import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Table as TRTable } from '@tanstack/react-table';
import { TableV2 } from '@anytag/shared-atoms';
import { TableData } from '@anytag/shared-types';
import { CampaignPost, CampaignPostsSummary } from '@anytag/graphql';
import { DownloadCsvPagesAndRowsModal, PostDiscoveryDetailsModal } from '@anytag/shared-molecules';
import { useAllCampaignPostsListTable, useDownloadCampaignPostsDiscovery } from './hooks';
import { formatCampaignPostsDiscoveryData } from './utils';
import { CampaignPostsDiscoveryRowData } from './types';

const MAX_CSV_ROWS_EXPORT_FOR_POST_DISCOVERY = 10_000;

export interface CampaignPostsDiscoveryListProps extends TableData<CampaignPost> {
  open?: boolean;
  onClose?: () => void;
  summary?: CampaignPostsSummary;
}

export const CampaignPostsDiscoveryList = ({
  open,
  onClose,
  loading,
  summary,
  data = [],
  totalRecords
}: CampaignPostsDiscoveryListProps) => {
  const { t } = useTranslation();
  const tableRef = useRef<TRTable<CampaignPostsDiscoveryRowData>>(null);
  const { columns } = useAllCampaignPostsListTable({
    total: totalRecords,
    currencyId: summary?.currencyId
  });
  const { limit, downloading, handleDownloadCsv } = useDownloadCampaignPostsDiscovery({
    onClose,
    visibleColumns: tableRef.current?.getVisibleFlatColumns() // this is already sorted visible columns instead of columnVisibility state
  });

  return (
    <TableV2
      loading={loading}
      columns={columns}
      rowHeight="112px"
      className="report-table"
      reactTableRef={tableRef}
      data={formatCampaignPostsDiscoveryData(data, summary)}
      empty={{ hideDescription: true, title: t('No campaigns has been found') }}
      hasColumnCustomization
      noTopBorderRadius
      hasBorder
    >
      <PostDiscoveryDetailsModal posts={data} />
      <DownloadCsvPagesAndRowsModal
        open={open}
        limit={limit}
        onClose={onClose}
        downloading={downloading}
        onSubmit={handleDownloadCsv}
        maxRows={MAX_CSV_ROWS_EXPORT_FOR_POST_DISCOVERY}
      />
    </TableV2>
  );
};
